import React from "react";
import {
  AiOutlineCalendar,
  AiOutlineCar,
  AiOutlineClockCircle,
  AiOutlineContainer,
  AiOutlineSolution,
  AiOutlineDollarCircle,
  AiOutlineFileText,
  AiOutlineMessage,
  AiOutlineEnvironment,
  AiOutlineSafetyCertificate,
  AiOutlineCompass,
} from "react-icons/ai";
import { GoBook } from "react-icons/go";
import { HiOutlineOfficeBuilding } from "react-icons/hi";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import axios from "axios";
import { serverUrl } from "../../nestserver";
import { selectUser } from "../../redux/slices/auth.slice";

const TONES = {
  "Admissions":           { card: "#ede9fe", circle: "#c4b5fd", icon: "#6d28d9" },
  "Fee":                  { card: "#dcfce7", circle: "#86efac", icon: "#15803d" },
  "Attendance":           { card: "#fef9c3", circle: "#fde047", icon: "#a16207" },
  "Report":               { card: "#cffafe", circle: "#67e8f9", icon: "#0e7490" },
  "Course Board":         { card: "#fdf4ff", circle: "#e9d5ff", icon: "#6b21a8" },
  "Schedule":             { card: "#ffe4e6", circle: "#fda4af", icon: "#be123c" },
  "Certificate Request":  { card: "#ecfdf5", circle: "#a7f3d0", icon: "#047857" },
  "Homework":             { card: "#e0f2fe", circle: "#7dd3fc", icon: "#0369a1" },

  "Diary":             { card: "#e0f2fe", circle: "#7dd3fc", icon: "#0369a1" },
  "Exam Result":          { card: "#eef2ff", circle: "#c7d2fe", icon: "#4338ca" },
  "Message":              { card: "#e0f2fe", circle: "#bae6fd", icon: "#0284c7" },
  "Transportation":       { card: "#f5f5f4", circle: "#d6d3d1", icon: "#44403c" },
  "Live Bus Tracking":    { card: "#fee2e2", circle: "#fca5a5", icon: "#b91c1c" },
  "Consent Forms":         { card: "#ecfeff", circle: "#a5f3fc", icon: "#0e7490" },
};

const CATEGORIES = [
  { label: "School",        items: ["Admissions", "Report", "Course Board", "Schedule"] },
  { label: "Academic",      items: ["Homework", "Diary", "Exam Result"] },
  { label: "Finance",       items: ["Fee"] },
  { label: "Daily",         items: ["Attendance", "Certificate Request", "Consent Forms"] },
  { label: "Communication", items: ["Message"] },
  { label: "Transport",     items: ["Transportation", "Live Bus Tracking"] },
];

const Menu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectUser);

  const schoolId = user?.schoolId || user?.parent?.school?._id || user?.school?._id;
  const school = useQuery(
    ["school", schoolId],
    () => axios.get(`${serverUrl}/school/${schoolId}`, { headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` } }).then(r => r.data),
    { enabled: !!schoolId }
  );

  const isTransportationEnabled = school?.data?.result?.data?.is_transportation_enabled !== false;
  const isLiveTrackingEnabled   = school?.data?.result?.data?.is_live_tracking_enabled   !== false;

  const allItems = [
    { title: "Admissions",           icon: <HiOutlineOfficeBuilding size={28} />, onClick: () => navigate("/dashboard/parent/menu/admission"),            url: "/dashboard/parent/menu/admission" },
    { title: "Fee",                  icon: <AiOutlineDollarCircle size={28} />,   onClick: () => navigate("/dashboard/parent/menu/fee"),                  url: "/dashboard/parent/menu/fee" },
    { title: "Attendance",           icon: <AiOutlineCalendar size={28} />,       onClick: () => navigate("/dashboard/parent/menu/attendance"),           url: "/dashboard/parent/menu/attendance" },
    { title: "Report",               icon: <AiOutlineFileText size={28} />,       onClick: () => navigate("/dashboard/parent/menu/report-card"),          url: "/dashboard/parent/menu/report-card" },
    { title: "Course Board",         icon: <GoBook size={28} />,                  onClick: () => navigate("/dashboard/parent/menu/course-board"),         url: "/dashboard/parent/menu/course-board" },
    { title: "Schedule",             icon: <AiOutlineClockCircle size={28} />,    onClick: () => navigate("/dashboard/parent/menu/schedule"),             url: "/dashboard/parent/menu/schedule" },
    { title: "Certificate Request",  icon: <AiOutlineSafetyCertificate size={28} />, onClick: () => navigate("/dashboard/parent/menu/manage-form"),        url: "/dashboard/parent/menu/manage-form" },
    { title: "Consent Forms",         icon: <AiOutlineCompass size={28} />,       onClick: () => navigate("/dashboard/parent/menu/consent-form"),                 url: "/dashboard/parent/menu/consent-form" },
    { title: "Homework",             icon: <AiOutlineContainer size={28} />,      onClick: () => navigate("/dashboard/parent/menu/homework"),             url: "/dashboard/parent/menu/homework" },
    { title: "Diary",             icon: <AiOutlineSolution size={28} />,      onClick: () => navigate("/dashboard/parent/menu/diary"),             url: "/dashboard/parent/menu/diary" },
    { title: "Exam Result",         icon: <AiOutlineSolution size={28} />,   onClick: () => navigate("/dashboard/parent/menu/assignment"),           url: "/dashboard/parent/menu/assignment" },
    { title: "Message",              icon: <AiOutlineMessage size={28} />,        onClick: () => navigate("/dashboard/parent/menu/message"),              url: "/dashboard/parent/menu/message" },
    { title: "Transportation",       icon: <AiOutlineCar size={28} />,            onClick: () => navigate("/dashboard/parent/menu/transportation"),       url: "/dashboard/parent/menu/transportation" },
    { title: "Live Bus Tracking",    icon: <AiOutlineEnvironment size={28} />,    onClick: () => navigate("/dashboard/parent/menu/live-tracking"),        url: "/dashboard/parent/menu/live-tracking" },
  ].filter((item) => {
    if (item.title === "Transportation"  && !isTransportationEnabled) return false;
    if (item.title === "Live Bus Tracking" && !isLiveTrackingEnabled)  return false;
    return true;
  });

  return (
    <div className="p-8">
      <div className="bg-white p-8 rounded-2xl">
        {CATEGORIES.map(({ label, items: titles }) => {
          const catItems = allItems.filter(i => titles.includes(i.title));
          if (!catItems.length) return null;
          return (
            <div key={label} style={{ marginBottom: 32 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14, paddingBottom: 8, borderBottom: "1px solid #f1f5f9" }}>
                {label}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                {catItems.map((item) => {
                  const tone = TONES[item.title] || { card: "#f1f5f9", circle: "#cbd5e1", icon: "#475569" };
                  const isActive = location.pathname.startsWith(item.url);
                  return (
                    <div
                      key={item.title}
                      onClick={item.onClick}
                      style={{
                        background: tone.card,
                        borderRadius: 20,
                        padding: "20px 12px 16px",
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 12,
                        boxShadow: isActive ? `0 0 0 2.5px ${tone.icon}` : "0 2px 8px rgba(0,0,0,0.06)",
                        transition: "all 0.15s ease",
                        minHeight: 120,
                      }}
                      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.11)"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = isActive ? `0 0 0 2.5px ${tone.icon}` : "0 2px 8px rgba(0,0,0,0.06)"; }}
                    >
                      <div style={{ width: 58, height: 58, borderRadius: "50%", background: tone.circle, display: "flex", alignItems: "center", justifyContent: "center", color: tone.icon, flexShrink: 0 }}>
                        {item.icon}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, textAlign: "center", lineHeight: 1.35, color: "#1e3a5f" }}>
                        {item.title}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Menu;
